import { Loader2 } from "lucide-react";
import type { CSSProperties } from "react";
import { useLocation } from "react-router-dom";

import { MentorShellPlaceholder } from "@/components/MentorShellPlaceholder";
import { useBootShellLoading } from "@/hooks/useBootShellLoading";
import { shouldUseMentorShellPlaceholder } from "@/lib/shellLoadingSurface";
import { cn } from "@/lib/utils";
import logoBlackInline from "@/assets/startup/folyamat-logo-transp-black.webp?inline";

interface BaseLoadingProps {
  className?: string;
  pathname?: string;
  style?: CSSProperties;
}

const LOGO_SRC = logoBlackInline;

export function AuthHandoffOverlay({ className }: BaseLoadingProps) {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      data-testid="auth-handoff-overlay"
      className={cn(
        "pointer-events-auto fixed inset-0 z-[1001] flex items-center justify-center bg-[#f6f2ea]",
        className,
      )}
    >
      <Loader2 className="h-8 w-8 animate-spin text-zinc-500" />
    </div>
  );
}

export function BootSplash({ className, style }: BaseLoadingProps) {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      data-testid="boot-splash"
      style={style}
      className={cn(
        "fixed inset-0 z-[1000] flex items-center justify-center overflow-hidden bg-white px-6 py-10",
        className,
      )}
    >
      <div className="relative z-10 flex w-full max-w-md flex-col items-center">
        <div
          data-testid="boot-splash-logo-frame"
          className="flex h-[90px] w-[90px] items-center justify-center overflow-hidden sm:h-[102px] sm:w-[102px]"
        >
          <img
            src={LOGO_SRC}
            alt="Folyamat"
            width={102}
            height={102}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-contain"
          />
        </div>
      </div>
    </div>
  );
}

export function ShellLoadingOverlay({ className, pathname }: BaseLoadingProps) {
  if (pathname && shouldUseMentorShellPlaceholder(pathname)) {
    return <MentorShellPlaceholder className={className} pathname={pathname} />;
  }

  return null;
}

export function RouteAwareShellLoadingOverlay({ className }: BaseLoadingProps) {
  const location = useLocation();
  return <ShellLoadingOverlay className={className} pathname={location.pathname} />;
}

export function BootAwareRouteShellLoading({ className }: BaseLoadingProps) {
  const { shouldRenderShellLoading, shellLoadingClassName } = useBootShellLoading();

  if (!shouldRenderShellLoading) {
    return null;
  }

  return (
    <RouteAwareShellLoadingOverlay className={cn(shellLoadingClassName, className)} />
  );
}

type AppShellLoadingOverlayVariant = "logo" | "shell";

interface AppShellLoadingOverlayProps extends BaseLoadingProps {
  variant?: AppShellLoadingOverlayVariant;
}

export function AppShellLoadingOverlay({
  variant = "shell",
  className,
}: AppShellLoadingOverlayProps) {
  if (variant === "logo") {
    return <BootSplash className={className} />;
  }

  return <RouteAwareShellLoadingOverlay className={className} />;
}
